import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useTenantRoles, useTenantSlaLadder, useSaveTenantSlaLadder } from "../../lib/api";
import { DEFAULT_ROLE_TERMS } from "../../lib/TenantConfigContext";
import { UNITS, ladderProblem, splitDelay, toSeconds } from "../../lib/slaLadder";
import {
  gatedStepKeys,
  isQuestion,
  questionFromSettings,
  questionProblem,
  settingsFromQuestion,
  stepAfterQuestion,
} from "../../lib/slaQuestion";
import SlaConditionNode from "./SlaConditionNode";
import {
  DetailHeader,
  GhostButton,
  PrimaryButton,
  SecondaryButton,
  Select,
  Toggle,
  inputClass,
} from "./primitives";

let tempId = 0;
const newRung = (seconds, role) => ({
  key: `new-${tempId++}`,
  ...splitDelay(seconds),
  target_type: role ? "role" : "group",
  target_role: role || null,
  template_name: "",
  label: "",
  enabled: true,
});

// A customer step in a ladder that asks questions: "has your issue been resolved?".
const newQuestion = (seconds) => ({
  ...newRung(seconds, null),
  target_type: "customer",
  target_role: null,
  qs: questionFromSettings(),
});

const TARGET_LABELS = {
  group: "The group",
  customer: "The customer",
  pending_members: "Whoever has not joined",
};

const toRungs = (data) =>
  (data?.rungs || []).map((r) => ({
    ...r,
    ...splitDelay(r.seconds),
    key: `db-${r.id}`,
    // What a question step holds: how long it waits for an answer, its reminders, and so on.
    ...(data?.meta?.conditional && r.target_type === "customer" ? { qs: questionFromSettings(r.settings) } : {}),
  }));

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

export default function SlaEditor({ tenantId, ladder = "response", title }) {
  const { data, isLoading } = useTenantSlaLadder(tenantId, ladder);
  const save = useSaveTenantSlaLadder(tenantId, ladder);
  // The selected brand's own roles, by their full names ("Workshop Manager",
  // not "WM"). Super Admin sits outside the brand's config, so the names come
  // from this brand's Roles & access list, not from whoever is signed in.
  const { data: rolesData } = useTenantRoles(tenantId);
  const roleName = (code) =>
    (rolesData?.rows || []).find((r) => r.code === code)?.label || DEFAULT_ROLE_TERMS[code]?.label || code;

  const [enabled, setEnabled] = useState(false);
  const [rungs, setRungs] = useState([]);
  const baseline = useMemo(() => ({ enabled: data?.enabled ?? false, rungs: toRungs(data) }), [data]);

  useEffect(() => {
    setEnabled(baseline.enabled);
    setRungs(baseline.rungs);
  }, [baseline]);

  const roles = data?.target_roles || [];
  // Which targets this ladder allows. A join ladder has no group option: the
  // premise is that people are not in the group yet.
  const targets = data?.meta?.targets || ["group", "role"];
  const nonRoleTargets = targets.filter((t) => t !== "role");
  // A ladder that asks questions: its customer steps are questions with a condition after them.
  const conditional = !!data?.meta?.conditional;

  // Sorted for display only; the delay is what actually orders the ladder, so
  // the list re-sorts as you type rather than keeping a separate position.
  // Compared in seconds, or "90 sec" would sort after "2 min".
  const ordered = useMemo(
    () => [...rungs].sort((a, b) => toSeconds(a) - toSeconds(b)),
    [rungs],
  );

  // The staff steps that wait for a "no" have no timer of their own.
  const gated = useMemo(() => (conditional ? gatedStepKeys(ordered) : new Set()), [conditional, ordered]);

  const dirty = enabled !== baseline.enabled || JSON.stringify(rungs) !== JSON.stringify(baseline.rungs);

  const update = (key, patch) =>
    setRungs((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addQuestion = () => {
    const last = ordered[ordered.length - 1];
    setRungs((rs) => [...rs, newQuestion(last ? toSeconds(last) + 300 : 300)]);
  };

  const addStep = () => {
    const last = ordered[ordered.length - 1];
    // A new step goes to the brand's first role, or to the group when this
    // ladder cannot target a role.
    const role = targets.includes("role") ? roles[0] : null;
    const rung = newRung(last ? toSeconds(last) + 300 : 300, role);
    setRungs((rs) => [...rs, role ? rung : { ...rung, target_type: nonRoleTargets[0] || "group" }]);
  };

  // The default ladder names roles every brand used to have; steps for a role
  // this brand does not have are left out rather than saved and refused.
  const useStarter = () =>
    setRungs(
      (data?.starter || [])
        .filter((s) => s.target_type !== "role" || roles.includes(s.target_role))
        .map((s, i) => ({
          ...newRung(s.seconds, null),
          key: `starter-${i}`,
          target_type: s.target_type,
          target_role: s.target_role,
          label: s.label,
          ...(conditional && s.target_type === "customer" ? { qs: questionFromSettings(s.settings) } : {}),
        })),
    );

  const onSave = async () => {
    const problem = ladderProblem(ordered);
    if (problem) {
      toast.error(problem);
      return;
    }
    if (conditional) {
      for (const [i, r] of ordered.entries()) {
        const bad = isQuestion(r) ? questionProblem(r.qs || questionFromSettings()) : "";
        if (bad) {
          toast.error(`Step ${i + 1}: ${bad}`);
          return;
        }
      }
    }
    try {
      await save.mutateAsync({
        enabled,
        rungs: ordered.map((r) => ({
          seconds: toSeconds(r),
          target_type: r.target_type,
          target_role: r.target_type === "role" ? r.target_role : null,
          template_name: (r.template_name || "").trim(),
          label: r.label || "",
          enabled: !!r.enabled,
          // Only a ladder that asks questions has step settings, and only on its questions.
          ...(conditional ? { settings: isQuestion(r) ? settingsFromQuestion(r.qs || questionFromSettings()) : {} } : {}),
        })),
      });
      toast.success("Escalations saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const onDiscard = () => {
    setEnabled(baseline.enabled);
    setRungs(baseline.rungs);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  return (
    <>
      <DetailHeader
        title={title}
        toggleLabel="Send escalations"
        status={enabled !== baseline.enabled ? "Not saved" : ""}
        checked={enabled}
        onChange={setEnabled}
      />
      <div className="flex flex-col gap-6">
        {!ordered.length ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-[#BBD4F1] px-4 py-10">
            <div className="text-[14px] font-medium text-[#111827]">No steps</div>
            <div className="flex items-center gap-2">
              <PrimaryButton onClick={useStarter}>Use default ladder</PrimaryButton>
              <SecondaryButton onClick={addStep}>Add step</SecondaryButton>
            </div>
          </div>
        ) : (
          <div>
            {ordered.map((r, i) => {
              const active = enabled && r.enabled;
              return (
                <Fragment key={r.key}>
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-[13px] font-semibold text-white shadow-[0_1px_2px_rgba(16,24,40,0.1)]"
                        style={{ background: active ? "#2667B5" : "#9CA3AF" }}
                      >
                        {i + 1}
                      </div>
                      {i < ordered.length - 1 && <div className="my-1 w-[2px] flex-1 rounded-full bg-[#8FB8E6]" />}
                    </div>
                    <div className="mb-4 min-w-0 flex-1 rounded-xl bg-white px-4 py-3 border border-[#EAECF0] shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                      <div className="flex flex-wrap items-center gap-3">
                        {gated.has(r.key) ? (
                          <span className="text-[13px] font-medium text-[#B42318]" data-testid={`gated-${r.key}`}>
                            When the customer answers No
                          </span>
                        ) : (
                          <>
                            <span className="text-[13px] font-medium text-[#374151]">After</span>
                            <input
                              type="number"
                              min="0"
                              className={`${inputClass} !w-[76px] text-right`}
                              value={r.amount}
                              onChange={(e) => update(r.key, { amount: e.target.value })}
                              aria-label={`Delay for step ${i + 1}`}
                            />
                            <Select
                              className="w-[94px]"
                              value={r.unit}
                              onChange={(e) => update(r.key, { unit: e.target.value })}
                              aria-label={`Delay unit for step ${i + 1}`}
                            >
                              {UNITS.map((u) => (
                                <option key={u.value} value={u.value}>
                                  {u.label}
                                </option>
                              ))}
                            </Select>
                          </>
                        )}
                        <span className="text-[13px] font-medium text-[#374151]">{conditional && isQuestion(r) ? "ask" : "send to"}</span>
                        <Select
                          className="w-[240px]"
                          value={r.target_type === "role" ? `role:${r.target_role}` : r.target_type}
                          onChange={(e) => {
                            const v = e.target.value;
                            update(
                              r.key,
                              v.startsWith("role:")
                                ? { target_type: "role", target_role: v.slice(5), qs: undefined }
                                : {
                                    target_type: v,
                                    target_role: null,
                                    // Aiming a step at the customer in this ladder makes it a question.
                                    qs: conditional && v === "customer" ? r.qs || questionFromSettings() : undefined,
                                  },
                            );
                          }}
                          aria-label={`Recipient for step ${i + 1}`}
                        >
                          {nonRoleTargets.map((t) => (
                            <option key={t} value={t}>
                              {TARGET_LABELS[t] || t}
                            </option>
                          ))}
                          {targets.includes("role") &&
                            roles.map((role) => (
                              <option key={role} value={`role:${role}`}>
                                {roleName(role)}
                              </option>
                            ))}
                        </Select>
                        <div className="ml-auto flex items-center gap-2">
                          <Toggle
                            checked={!!r.enabled}
                            onChange={(v) => update(r.key, { enabled: v })}
                            label={`Step ${i + 1} active`}
                          />
                          <button
                            type="button"
                            onClick={() => setRungs((rs) => rs.filter((x) => x.key !== r.key))}
                            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                            aria-label={`Remove step ${i + 1}`}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                      <input
                        className={`${inputClass} mt-3`}
                        value={r.template_name || ""}
                        placeholder={conditional && isQuestion(r) ? "Template that asks: has your issue been resolved?" : "Template name"}
                        onChange={(e) => update(r.key, { template_name: e.target.value })}
                        aria-label={`Template for step ${i + 1}`}
                      />
                    </div>
                  </div>
                  {conditional && isQuestion(r) && (
                    <SlaConditionNode
                      id={r.key}
                      q={r.qs || questionFromSettings()}
                      onChange={(patch) => update(r.key, { qs: { ...(r.qs || questionFromSettings()), ...patch } })}
                      next={(() => {
                        const after = stepAfterQuestion(ordered, r);
                        return after
                          ? {
                              position: ordered.findIndex((x) => x.key === after.key) + 1,
                              name: after.target_type === "role" ? roleName(after.target_role) : TARGET_LABELS[after.target_type] || after.target_type,
                            }
                          : null;
                      })()}
                    />
                  )}
                </Fragment>
              );
            })}

            <div className="flex flex-wrap items-center gap-2 pl-12">
              <SecondaryButton onClick={addStep}>
                <PlusIcon /> Add step
              </SecondaryButton>
              {conditional && (
                <SecondaryButton onClick={addQuestion}>
                  <PlusIcon /> Add question
                </SecondaryButton>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save changes
          </PrimaryButton>
          {dirty && <GhostButton onClick={onDiscard}>Discard</GhostButton>}
        </div>
      </div>
    </>
  );
}
